import { orderByKeys } from '@/Helpers/sorters';
import storage from '@/Helpers/storage';
import moment from 'moment';
import StudyTaskService from '@/Services/StudySchema/StudyTaskService';
import { CURRENT_USER_GETTERS } from '@/store/modules/currentuser';
import {
  STUDY_TASK_ACTIONS,
  STUDY_TASK_GETTERS,
  STUDY_TASK_MUTATIONS
} from './definitions';
import StudyTaskModel from '@/Models/StudyTaskModel';

class State {
  constructor() {
    /** @type {StudyTaskModel[]} array of study tasks */
    this.list = [];
    this.activeStudyTask = null;
    this.showStudyTaskModal = false;
  }
}

const store = {
  namespaced: true,
  state: new State(),
  mutations: {},
  actions: {},
  getters: {}
};

/** @type {import('vuex').MutationTree<typeof store.state>} */
store.mutations = {
  /**
   * Set study tasks
   * @param {StudyTaskModel[]} studyTasks
   */
  [STUDY_TASK_MUTATIONS.SET_STUDY_TASKS](state, studyTasks) {
    state.list = studyTasks;
  },

  /**
   * Set active study task
   * @param {StudyTaskModel} studyTask
   */
  [STUDY_TASK_MUTATIONS.SET_ACTIVE_STUDY_TASK](state, studyTask) {
    state.activeStudyTask = studyTask;
  },

  /**
   * Set show study task modal
   * @param {StudyTaskModel} studyTask
   */
  [STUDY_TASK_MUTATIONS.SET_SHOW_STUDY_TASK_MODAL](state, show) {
    state.showStudyTaskModal = show;
  }
};

/** @type {import('vuex').ActionTree<typeof store.state>} */
store.actions = {
  [STUDY_TASK_ACTIONS.GET_MY_STUDY_TASKS]({ commit }) {
    return StudyTaskService.getMyStudyTasks().then(studyTasks => {
      commit(STUDY_TASK_MUTATIONS.SET_STUDY_TASKS, studyTasks);
      return studyTasks;
    });
  },
  [STUDY_TASK_ACTIONS.GET_MY_NEXT_STUDY_TASK]({ dispatch }) {
    return StudyTaskService.getMyNextStudyTask().then(studyTask => {
      dispatch(STUDY_TASK_ACTIONS.SET_ACTIVE_STUDY_TASK, studyTask);
      return studyTask;
    });
  },
  [STUDY_TASK_ACTIONS.SET_ACTIVE_STUDY_TASK]({ commit }, studyTask) {
    storage.setEncrypted(`studytask/active`, studyTask);
    commit(STUDY_TASK_MUTATIONS.SET_ACTIVE_STUDY_TASK, studyTask);
  },
  [STUDY_TASK_ACTIONS.GET_ACTIVE_STUDY_TASK]({ commit }) {
    const studyTask = storage.getEncrypted(`studytask/active`);
    let activeStudyTask = null;

    if (studyTask) {
      activeStudyTask = new StudyTaskModel(studyTask);
    }

    commit(STUDY_TASK_MUTATIONS.SET_ACTIVE_STUDY_TASK, activeStudyTask);
  },
  [STUDY_TASK_ACTIONS.CLEAR_ACTIVE_STUDY_TASK]({ commit }) {
    storage.remove(`studytask/active`);
    commit(STUDY_TASK_MUTATIONS.SET_ACTIVE_STUDY_TASK, null);
  }
};

/** @type {import('vuex').GetterTree<typeof store.state>} */
store.getters = {
  [STUDY_TASK_GETTERS.MY_STUDY_TASKS](state) {
    return state.list;
  },

  [STUDY_TASK_GETTERS.ACTIVE_STUDY_TASK](state) {
    return state.activeStudyTask;
  },

  [STUDY_TASK_GETTERS.SHOW_STUDY_TASK_MODAL](state) {
    return state.showStudyTaskModal;
  },

  [STUDY_TASK_GETTERS.MY_STUDY_TASKS_TODO_NOW_BY_GROUP]:
    state =>
    (orgId = null, groupId = null, joinedDate) => {
      let tasks = state.list.filter(st => !st.Done);
      const daysSinceJoin = moment().diff(joinedDate, 'days');

      if (orgId !== null && groupId !== null) {
        tasks = tasks.filter(
          g => g.OrganizationId === orgId && g.GroupId == groupId
        );
      }

      tasks = tasks.filter(
        st =>
          !st.Done &&
          daysSinceJoin >= st.DaysAfterInclusion &&
          daysSinceJoin <= st.DaysAfterInclusion + st.AllowedDaysAfter
      );

      tasks = orderByKeys(
        tasks,
        ['DaysAfterInclusion', 'Priority'],
        ['asc', 'asc']
      );

      return tasks;
    },

  [STUDY_TASK_GETTERS.MY_STUDY_TASKS_TODO_NOW](
    state,
    getters,
    rootState,
    rootGetters
  ) {
    const groups =
      rootGetters[
        `currentuser/${CURRENT_USER_GETTERS.MY_GROUPS_WITH_PATIENT_ROLE}`
      ];

    let tasks = [];

    // Messy method call but we retrieve all tasks that are due for each group
    // and return all.
    groups.forEach(group => {
      const groupTasks = getters[
        STUDY_TASK_GETTERS.MY_STUDY_TASKS_TODO_NOW_BY_GROUP
      ](group.OrganizationId, group.Id, group.PatientJoinedDate);

      tasks = tasks.concat(groupTasks);
    });

    return tasks;
  }
};

export {
  STUDY_TASK_ACTIONS,
  STUDY_TASK_GETTERS,
  STUDY_TASK_MUTATIONS
} from './definitions';

export default store;
